import { KNOWN_TOLL_PATHS, TOLL_PERIODS } from "./constants";
import { formatRoute } from "./helpers";

const EMPTY_TOLL_DISPLAY = "—";
const SIGNED_DECIMAL_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;
const STATIC_TOLL_FIELDS = new Set(["id", "period"]);
const TOLL_ROW_PREFIX_PATTERN = /^(tvb|vtp|avb)_/;

/**
 * @typedef {"half" | "full"} TollSectionLayout
 */

/**
 * @typedef {Object} TollTableDefinition
 * @property {"tvb" | "vtp" | "avb"} key
 * @property {string} title
 * @property {TollSectionLayout} layout
 * @property {string[]} knownPaths
 * @property {(path: string) => boolean} matchesPath
 */

/**
 * @typedef {Object.<string, string | null | undefined>} TollApiRow
 * @property {string} dtime
 * @property {string} toll_path
 */

/**
 * @typedef {Object.<string, string>} TollGridRow
 * @property {string} id
 * @property {string} period
 */

/**
 * @typedef {Object} TollTableSection
 * @property {string} key
 * @property {string} title
 * @property {TollSectionLayout} layout
 * @property {Array<Object>} columns
 * @property {TollGridRow[]} rows
 */

/**
 * @typedef {Object} TollCellChange
 * @property {string} rowId
 * @property {string} field
 * @property {string} value
 */

/**
 * Declarative definition of the toll grids rendered by the parameters screen.
 * Each definition knows:
 * - which known routes must always be visible
 * - how to recognize routes that already exist in the API payload
 * - how the section should be laid out in the UI
 *
 * @type {TollTableDefinition[]}
 */
export const TOLL_TABLE_DEFINITIONS = [
  {
    key: "tvb",
    title: "TVB ↔ PVB",
    layout: "half",
    knownPaths: KNOWN_TOLL_PATHS.tvb,
    matchesPath: (path) => {
      const lowerPath = path.toLowerCase();
      if (lowerPath.includes("tvb-tvb") || lowerPath.includes("tvb - tvb")) {
        return false;
      }
      return lowerPath.includes("tvb") || lowerPath.includes("pvb-tvb");
    },
  },
  {
    key: "vtp",
    title: "VTP ↔ PVB",
    layout: "half",
    knownPaths: KNOWN_TOLL_PATHS.vtp,
    matchesPath: (path) =>
      path.toLowerCase().includes("vtp") && !path.toLowerCase().includes("tvb"),
  },
  {
    key: "avb",
    title: "AVB ↔ PVB",
    layout: "full",
    knownPaths: KNOWN_TOLL_PATHS.avb,
    matchesPath: (path) =>
      path.toLowerCase().includes("avb") && !path.toLowerCase().includes("tvb"),
  },
];

/**
 * Normalizes a toll cell value into the text representation used across the UI.
 * The grid works with decimal strings to avoid locale formatting side effects
 * and unintended rounding while the user edits values.
 *
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
export const normalizeTollValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value).trim().replace(",", ".");
};

/**
 * Formats a toll value for read-only display in the grid.
 *
 * Empty strings in the row model mean that there is no persisted toll value
 * yet for that path and period. They are rendered as an em dash so users can
 * distinguish the absence of data from an explicit persisted zero, which must
 * remain visible as `0`.
 *
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
export const formatDisplayedTollValue = (value) => {
  const normalizedValue = normalizeTollValue(value);
  return normalizedValue === "" ? EMPTY_TOLL_DISPLAY : normalizedValue;
};

/**
 * Validates a toll value using the same accepted decimal syntax as the backend
 * and returns its canonical string representation when it is valid.
 *
 * Empty strings are allowed because they represent a deliberate SQL `NULL`.
 * This helper is used only during save planning so the UI can preserve the raw
 * edited text until the user decides to persist the change.
 *
 * @param {string | number | null | undefined} value
 * @returns {{ value: string, isValid: boolean }}
 */
export const normalizeAndValidateTollValue = (value) => {
  const normalizedValue = normalizeTollValue(value);

  if (normalizedValue === "") {
    return { value: "", isValid: true };
  }

  if (!SIGNED_DECIMAL_PATTERN.test(normalizedValue)) {
    return { value: normalizedValue, isValid: false };
  }

  const canonicalValue = normalizedValue
    .replace(/^\./, "0.")
    .replace(/^-\./, "-0.")
    .replace(/\.$/, "");

  if (canonicalValue.startsWith("-")) {
    return { value: canonicalValue, isValid: false };
  }

  return { value: canonicalValue, isValid: true };
};

/**
 * Builds a stable key for a single editable cell in the toll matrices.
 *
 * @param {string} rowId
 * @param {string} field
 * @returns {string}
 */
export const getTollCellKey = (rowId, field) => `${rowId}::${field}`;

/**
 * Returns the editable path fields of a toll row, excluding static metadata
 * such as the row id and the period label.
 *
 * @param {Record<string, unknown>} row
 * @returns {string[]}
 */
export const getEditableTollFields = (row) =>
  Object.keys(row).filter((field) => !STATIC_TOLL_FIELDS.has(field));

/**
 * Normalizes every editable value in a toll grid row so the hook can keep a
 * consistent row shape regardless of how the DataGrid parsed the edition.
 *
 * @param {Record<string, unknown>} row
 * @returns {Record<string, string>}
 */
export const normalizeTollRow = (row) => {
  const normalizedRow = { ...row };

  getEditableTollFields(normalizedRow).forEach((field) => {
    normalizedRow[field] = normalizeTollValue(normalizedRow[field]);
  });

  return normalizedRow;
};

/**
 * Creates the static first column used by every toll grid.
 *
 * @returns {Object}
 */
const buildPeriodColumn = () => ({
  field: "period",
  headerName: "Peaje",
  width: 150,
  editable: false,
  sortable: false,
  filterable: false,
  headerAlign: "center",
  renderHeader: () => (
    <div style={{ textAlign: "center", fontWeight: 600 }}>
      <div>Peaje</div>
      <div>(€/MWh)</div>
    </div>
  ),
  renderCell: (params) => <div style={{ fontWeight: 600 }}>{params.value}</div>,
});

/**
 * Creates the editable column definition for a toll path.
 *
 * @param {string} path
 * @returns {Object}
 */
const buildPathColumn = (path) => ({
  field: path,
  headerName: formatRoute(path),
  flex: 1,
  editable: true,
  sortable: false,
  filterable: false,
  type: "number",
  valueParser: (value) => normalizeTollValue(value),
  valueFormatter: (value) => formatDisplayedTollValue(value),
  headerAlign: "center",
  align: "center",
});

/**
 * Collapses the yearly toll payload into a single latest record per path.
 *
 * The parameters screen edits one value per route and period. If the API ever
 * returns multiple rows for the same `toll_path` within the selected year, the
 * UI must display and update the most recent one consistently.
 *
 * @param {TollApiRow[]} data
 * @returns {Map<string, TollApiRow>}
 */
export const buildLatestTollEntryMap = (data) => {
  const latestEntries = new Map();

  data.forEach((item) => {
    const currentEntry = latestEntries.get(item.toll_path);

    if (!currentEntry || new Date(item.dtime) > new Date(currentEntry.dtime)) {
      latestEntries.set(item.toll_path, item);
    }
  });

  return latestEntries;
};

/**
 * Builds the route-to-date map used for updates from the latest yearly toll
 * snapshot.
 *
 * @param {TollApiRow[]} data
 * @returns {Map<string, string>}
 */
export const buildTollPathDateMap = (data) =>
  new Map(
    Array.from(buildLatestTollEntryMap(data), ([path, entry]) => [
      path,
      entry.dtime,
    ])
  );

/**
 * Builds the five toll-period rows for a single table section.
 *
 * @param {string[]} paths
 * @param {string} tableKey
 * @param {TollApiRow[]} data
 * @returns {TollGridRow[]}
 */
const buildTollRows = (paths, tableKey, latestEntriesByPath) =>
  TOLL_PERIODS.map((period) => {
    const row = {
      id: `${tableKey}_${period.key}`,
      period: period.label,
    };

    paths.forEach((path) => {
      const pathData = latestEntriesByPath.get(path);
      row[path] = normalizeTollValue(pathData?.[period.key]);
    });

    return row;
  });

/**
 * Merges the routes that must always be visible in the UI with the routes
 * actually returned by the API for the selected year.
 *
 * @param {string[]} knownPaths
 * @param {string[]} existingPaths
 * @param {(path: string) => boolean} matchesPath
 * @returns {string[]}
 */
const mergeKnownAndExistingPaths = (knownPaths, existingPaths, matchesPath) => {
  const matchingExistingPaths = existingPaths.filter(matchesPath);
  return [...new Set([...knownPaths, ...matchingExistingPaths])];
};

/**
 * Transforms the backend toll payload into the declarative sections consumed by
 * the screen. This keeps the hook focused on orchestration and persistence.
 *
 * @param {TollApiRow[]} data
 * @returns {TollTableSection[]}
 */
export const buildTollTableSections = (data) => {
  const latestEntriesByPath = buildLatestTollEntryMap(data);
  const existingPaths = [...latestEntriesByPath.keys()];

  return TOLL_TABLE_DEFINITIONS.map((definition) => {
    const paths = mergeKnownAndExistingPaths(
      definition.knownPaths,
      existingPaths,
      definition.matchesPath
    );

    return {
      key: definition.key,
      title: definition.title,
      layout: definition.layout,
      columns: [buildPeriodColumn(), ...paths.map(buildPathColumn)],
      rows: buildTollRows(paths, definition.key, latestEntriesByPath),
    };
  });
};

/**
 * Stores the initial value of every editable cell so later edits can be
 * compared at cell level instead of row level.
 *
 * @param {TollTableSection[]} tableSections
 * @returns {Map<string, string>}
 */
export const buildTollBaselineCellMap = (tableSections) => {
  const baselineCellMap = new Map();

  tableSections.forEach((section) => {
    section.rows.forEach((row) => {
      getEditableTollFields(row).forEach((field) => {
        baselineCellMap.set(
          getTollCellKey(row.id, field),
          normalizeTollValue(row[field])
        );
      });
    });
  });

  return baselineCellMap;
};

/**
 * Computes the next set of modified cells after the user edits a row in the
 * grid. The comparison is performed per cell against the baseline snapshot.
 *
 * @param {Object} params
 * @param {Map<string, TollCellChange>} params.previousModifiedCells
 * @param {Record<string, unknown>} params.updatedRow
 * @param {Map<string, string>} params.baselineCellMap
 * @returns {Map<string, TollCellChange>}
 */
export const getModifiedCellsAfterRowUpdate = ({
  previousModifiedCells,
  updatedRow,
  baselineCellMap,
}) => {
  const nextModifiedCells = new Map(previousModifiedCells);

  getEditableTollFields(updatedRow).forEach((field) => {
    const cellKey = getTollCellKey(updatedRow.id, field);
    const nextValue = normalizeTollValue(updatedRow[field]);
    const originalValue = normalizeTollValue(baselineCellMap.get(cellKey));

    if (nextValue === originalValue) {
      nextModifiedCells.delete(cellKey);
      return;
    }

    nextModifiedCells.set(cellKey, {
      rowId: updatedRow.id,
      field,
      value: nextValue,
    });
  });

  return nextModifiedCells;
};

const getPeriodKeyFromRowId = (rowId) =>
  rowId.replace(TOLL_ROW_PREFIX_PATTERN, "");

/**
 * Generates the human-readable label shown when a modified toll cell contains
 * an invalid numeric value.
 *
 * @param {string} field
 * @param {string} rowId
 * @returns {string}
 */
const formatInvalidValueLabel = (field, rowId) => {
  const periodKey = getPeriodKeyFromRowId(rowId);
  const periodLabel =
    TOLL_PERIODS.find((period) => period.key === periodKey)?.label ?? periodKey;

  return `${formatRoute(field)} (${periodLabel})`;
};

/**
 * Builds the persistence plan for the current edition session.
 * The output separates:
 * - invalid edited cells
 * - rows to create for routes without previous data in the selected year
 * - partial updates for existing routes
 *
 * Only modified cells are considered, which prevents accidental persistence of
 * untouched values from the same visual row.
 *
 * @param {Object} params
 * @param {Map<string, TollCellChange>} params.modifiedCells
 * @param {Map<string, string>} params.pathDateMap
 * @param {string} params.selectedYear
 * @returns {{ invalidValues: string[], toCreate: Object[], toUpdate: Object[] }}
 */
export const buildTollSavePlan = ({
  modifiedCells,
  pathDateMap,
  selectedYear,
}) => {
  const toCreateMap = new Map();
  const toUpdate = [];
  const invalidValues = [];

  modifiedCells.forEach(({ rowId, field, value }) => {
    const { value: normalizedValue, isValid } =
      normalizeAndValidateTollValue(value);

    if (!isValid) {
      invalidValues.push(formatInvalidValueLabel(field, rowId));
      return;
    }

    const periodKey = getPeriodKeyFromRowId(rowId);
    const payloadValue = normalizedValue === "" ? null : normalizedValue;
    const dtime = pathDateMap.get(field);

    if (dtime) {
      toUpdate.push({ toll_path: field, dtime, [periodKey]: payloadValue });
      return;
    }

    if (!toCreateMap.has(field)) {
      toCreateMap.set(field, {
        toll_path: field,
        dtime: `${selectedYear}-01-01`,
      });
    }

    toCreateMap.get(field)[periodKey] = payloadValue;
  });

  return {
    invalidValues,
    toCreate: Array.from(toCreateMap.values()),
    toUpdate,
  };
};
